import { motion, AnimatePresence } from "framer-motion";
import { useExitIntentLogic } from "@/hooks/useExitIntentLogic";
import { EarlyExitPopup } from "./popups/EarlyExitPopup";
import { FreeCroAnalysisPopup } from "./popups/FreeCroAnalysisPopup";
import { FreePlanPopup } from "./popups/FreePlanPopup";
import { NewsletterPopup } from "./popups/NewsletterPopup";
import { WalkthroughPopup } from "./popups/WalkthroughPopup";
import { SignupCreditsPopup } from "./popups/SignupCreditsPopup";

interface ExitIntentPopupProps {
  /** Pages where popup should appear */
  enabledPages?: string[];
  /** Delay before tracking exit intent (ms) */
  delay?: number;
}

export function ExitIntentPopup({ 
  enabledPages = ["/", "/blog", "/pricing", "/features", "/solutions", "/how-it-works", "/agencies"],
  delay = 2000 
}: ExitIntentPopupProps) {
  const {
    isVisible,
    popupType,
    closePopup,
    markNewsletterSubscribed,
  } = useExitIntentLogic({ enabledPages, delay });

  const renderPopupContent = () => {
    switch (popupType) {
      case 'signup_credits':
        return <SignupCreditsPopup onClose={closePopup} />;
      case 'early_exit':
        return <EarlyExitPopup onClose={closePopup} />;
      case 'walkthrough':
        return <WalkthroughPopup onClose={closePopup} />;
      case 'free_cro_analysis':
        return <FreeCroAnalysisPopup onClose={closePopup} />;
      case 'free_plan':
        return <FreePlanPopup onClose={closePopup} />;
      case 'newsletter':
        return <NewsletterPopup onClose={closePopup} onSuccess={markNewsletterSubscribed} />;
      default:
        return null;
    }
  };

  return (
    <AnimatePresence>
      {isVisible && popupType && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
          onClick={closePopup}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ type: "spring", damping: 22, stiffness: 280 }}
            role="dialog"
            aria-modal="true"
            className="relative w-full max-w-[720px] overflow-hidden rounded-[22px] border border-[hsl(var(--ec-line-strong))] bg-card text-foreground shadow-[0_60px_140px_-50px_rgba(0,0,0,0.95)]"
            onClick={(e) => e.stopPropagation()}
          >
            {renderPopupContent()}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
